// src/services/model_api.js
//
// Admin-only client for the backend model router (routers/model.py).
// Backend exposes only list / upload / activate — there is NO in-place edit
// endpoint, so "updating" a prompt is implemented as: upload a NEW version,
// then activate it (see ModelConfigPanel.vue). All endpoints require an admin
// bearer token, which apiClient attaches (and auto-refreshes) already.
import { apiClient } from "./api_client";
import { FALLBACK_FAMILIES, FALLBACK_MODEL_GROUPS } from "../utils/models";

// Resolved once per page load and shared by both admin panels; the vocabulary
// cannot change without a backend deploy.
let optionsPromise = null;

const modelApi = {
  // GET /model/list — filters are sent as query params. The backend reads
  // ModelQuery via Depends(), so its optional fields (group_id, name,
  // model_group, family, owner, activated) map to the query string. (A request
  // body can't be used here: browsers drop GET bodies.)
  listModels: async (query = {}) => {
    try {
      const response = await apiClient.get("/model/list", { params: query });
      // Backend returns either { models: [...] } or { message: "Not an admin user" }
      return response.data?.models ?? [];
    } catch (error) {
      console.error("Error listing models:", error);
      throw error;
    }
  },

  // POST /model/upload — creates a new model row. The backend FORCES
  // activated=false regardless of payload, so callers must activate separately.
  // Returns a { message } string describing success or the validation failure.
  uploadModel: async (payload) => {
    try {
      const response = await apiClient.post("/model/upload", payload);
      return response.data; // { message: "A new model was created with id: ..." }
    } catch (error) {
      console.error("Error uploading model:", error);
      throw error;
    }
  },

  // GET /model/options — { families, model_groups } derived server-side from the
  // dispatch table, so the panels never restate it. Returns { message } for a
  // non-admin caller.
  getOptions: async () => {
    const response = await apiClient.get("/model/options");
    return response.data;
  },

  // Cached, fallback-safe wrapper around getOptions. A backend predating this
  // route 404s, and a non-admin gets a { message } body with no lists; either
  // way we fall back to the compiled-in vocabulary so the dropdowns populate.
  loadOptions: () => {
    if (!optionsPromise) {
      optionsPromise = modelApi
        .getOptions()
        .then((data) => ({
          families: data?.families?.length ? data.families : FALLBACK_FAMILIES,
          modelGroups: data?.model_groups?.length ? data.model_groups : FALLBACK_MODEL_GROUPS,
          fromBackend: !!data?.families?.length,
        }))
        .catch((error) => {
          console.warn(
            "GET /model/options unavailable, using built-in lists:",
            error?.message || error
          );
          return {
            families: FALLBACK_FAMILIES,
            modelGroups: FALLBACK_MODEL_GROUPS,
            fromBackend: false,
          };
        });
    }
    return optionsPromise;
  },

  // POST /model/activate — deactivates the currently active model in the group
  // and activates `name`. For live groups (core/self_help/end_detection) it also
  // closes active WebSocket connections so clients reconnect on the new model.
  activateModel: async ({ group_id, name, model_group }) => {
    try {
      const response = await apiClient.post("/model/activate", {
        group_id,
        name,
        model_group,
      });
      return response.data; // { message: "... deactivated and ... is activated" }
    } catch (error) {
      console.error("Error activating model:", error);
      throw error;
    }
  },
};

export default modelApi;
